import { useEffect, useState } from 'react';
import { useTranslation } from 'react-i18next';
import { useRouter } from 'next/router';
import {
  useApolloClient,
  useLazyQuery,
  useMutation,
  useQuery,
} from '@apollo/client';
import type {
  Ad,
  MutationResponse,
  PlatformSource,
} from '@/api/types/common.types';
import {
  GET_CURRENT_USER_COUNTERS,
  GET_MY_AD_DELETE_REASONS,
  GET_MY_ADS_LIST,
} from '@/features/Profile/sections/MyAds/api';
import type {
  CurrentUserCountersResponse,
  MyAdDeleteReasonsResponse,
  MyAdDeleteReasonsVariables,
} from '@/features/Profile/sections/MyAds/api';
import type { MyAdCardActionHandlers } from '@/features/Profile/sections/MyAds/components/Ads/Ads.helpers';
import { tabs } from '@/features/Profile/sections/MyAds/components/Ads/Ads.constants';
import { ROUTES } from '@/constants/routes';
import { AdStatus } from '@/types';
import { ARCHIVE_AD, DESTROY_AD } from '@/api/mutations/ads.mutations';
import {
  EditPopupType,
  type RemoveAdPayload,
} from '@/components/Ad/Settings/Settings.types';
import { usePlatformSource } from '@/hooks/usePlatformSource';
import type {
  DeleteReason,
  GetAdHasEditPendingApprovalResponse,
  PayForAdByPrepaidBundleResponse,
} from '@/api/types/ads.types';
import { useCustomPush } from '@/components/CustomLink/CustomLink.hooks';
import { QUERY } from '@/constants/common';
import { EDIT_AD_PAGE_SLUG } from '@/hooks/usePageType/usePageType.constants';
import { GET_AD_HAS_EDIT_PENDING_APPROVAL } from '@/api/queries/ads.queries';
import { resetHasEditPendingApproval } from '@/components/Ad/Settings/Settings.helpers';
import { usePreviousValue } from '@/hooks/usePreviousValue';
import { useToggleOpenState } from '@/hooks/useToggleOpenState';
import { PAY_FOR_AD_BY_PREPAID_BUNDLE } from '@/api/mutations/payments.mutations';
import { BACKEND_ERRORS } from '@/constants/backendErrors';
import { GET_USER } from '@/api/queries/user.queries';
import type { GetUserResponse } from '@/api/types/user.types';
import { useSelectionModeStore } from '@/store/useSelectionModeStore';
import { GET_BALANCE } from '@/features/Profile/sections/Balance/api';

type DestroyAdResponse = {
  destroyAd: MutationResponse<boolean>;
};

type DestroyAdVariables = {
  adId: string;
  deleteReasonId: string;
  deleteReasonDescription: string;
  source: PlatformSource;
};

type ArchiveAdResponse = {
  archiveAd: MutationResponse<boolean>;
};

type ArchiveAdVariables = {
  adId: string;
};

export const SelectionModes = {
  None: null,
  Restore: 'restore',
  BulkPromotion: 'bulk-promotion',
} as const;

export type SelectionMode =
  (typeof SelectionModes)[keyof typeof SelectionModes];

export function useMyAdsSection() {
  const { t } = useTranslation();
  const router = useRouter();
  const customPush = useCustomPush();
  const client = useApolloClient();
  const source = usePlatformSource();
  const [promotionAd, setPromotionAd] = useState<Ad | null>(null);
  const [payByBalanceAd, setPayByBalanceAd] = useState<Ad | null>(null);
  const [removingAd, setRemovingAd] = useState<Ad | null>(null);
  const [archivingAd, setArchivingAd] = useState<Ad | null>(null);
  const [isEditPendingApprovalOpen, toggleEditPendingApproval] =
    useToggleOpenState();
  const [isPayByBalanceOpen, togglePayByBalance] = useToggleOpenState();
  const [isSelectionModeActive, setIsSelectionModeActive] =
    useSelectionModeStore(state => [state.isActive, state.setIsActive]);
  const [selectionMode, setSelectionMode] = useState<SelectionMode>(
    SelectionModes.None
  );
  const isRestoreMode =
    isSelectionModeActive && selectionMode === SelectionModes.Restore;
  const isBulkPromotionMode =
    isSelectionModeActive && selectionMode === SelectionModes.BulkPromotion;
  const [deleteReasons, setDeleteReasons] = useState<DeleteReason[]>([]);
  const tabItems = tabs(t);
  const currentPath = router.asPath.split('?')[0].split('#')[0];
  const selectedAdStatus =
    tabItems.find(tab => ROUTES.profileAds(tab.value) === currentPath)?.value ??
    AdStatus.Published;
  const previousSelectedAdStatus = usePreviousValue(selectedAdStatus);

  const {
    data: countersData,
    loading: isAdsCountersLoading,
    refetch: refetchCounters,
  } = useQuery<CurrentUserCountersResponse>(GET_CURRENT_USER_COUNTERS, {
    fetchPolicy: 'network-only',
  });
  const { data: userInfo, refetch: refetchUserInfo } =
    useQuery<GetUserResponse>(GET_USER, {
      errorPolicy: 'all',
    });

  const [destroyAd] = useMutation<DestroyAdResponse, DestroyAdVariables>(
    DESTROY_AD
  );
  const [archiveAdMutation, { loading: isArchiveAdLoading }] = useMutation<
    ArchiveAdResponse,
    ArchiveAdVariables
  >(ARCHIVE_AD);
  const [getDeleteReasons, { loading: isDeleteReasonsLoading }] = useLazyQuery<
    MyAdDeleteReasonsResponse,
    MyAdDeleteReasonsVariables
  >(GET_MY_AD_DELETE_REASONS, {
    fetchPolicy: 'network-only',
  });
  const [
    checkHasEditPendingApproval,
    { loading: isEditPendingApprovalLoading },
  ] = useLazyQuery<GetAdHasEditPendingApprovalResponse>(
    GET_AD_HAS_EDIT_PENDING_APPROVAL,
    {
      errorPolicy: 'all',
      fetchPolicy: 'no-cache',
    }
  );
  const [payForAdByPrepaidBundle, { loading: isPayByBalanceLoading }] =
    useMutation<PayForAdByPrepaidBundleResponse, { adId: string }>(
      PAY_FOR_AD_BY_PREPAID_BUNDLE
    );

  const counters = countersData?.currentUserCounters;
  const adStatusCounts = {
    [AdStatus.Archived]: counters?.archived ?? 0,
    [AdStatus.AwaitingPayment]: counters?.awaitingPayment ?? 0,
    [AdStatus.Expired]: counters?.expired ?? 0,
    [AdStatus.Pending]: counters?.pending ?? 0,
    [AdStatus.Published]: counters?.published ?? 0,
    [AdStatus.Rejected]: counters?.rejected ?? 0,
  };
  const canBulkPromote =
    Boolean(userInfo?.currentUser?.features?.servicesFeatures?.bulkEnabled) &&
    selectedAdStatus === AdStatus.Published &&
    adStatusCounts[AdStatus.Published] > 1;

  const shopPaidAdsBalance = userInfo?.currentUser?.shopPaidAdsBalance ?? 0;
  const canBulkRestore =
    Boolean(userInfo?.currentUser?.isActiveShop) &&
    selectedAdStatus === AdStatus.Expired &&
    adStatusCounts[AdStatus.Expired] > 0;

  useEffect(() => {
    if (!previousSelectedAdStatus) return;
    if (previousSelectedAdStatus === selectedAdStatus) return;

    refetchCounters();
  }, [previousSelectedAdStatus, refetchCounters, selectedAdStatus]);

  useEffect(() => {
    return () => {
      setIsSelectionModeActive(false);
      setSelectionMode(SelectionModes.None);
    };
  }, [setIsSelectionModeActive, setSelectionMode]);

  function closePromotionFlow() {
    setPromotionAd(null);
  }

  function openBulkPromotion() {
    setSelectionMode(SelectionModes.BulkPromotion);
    setIsSelectionModeActive(true);
  }

  function closeBulkPromotion() {
    setSelectionMode(SelectionModes.None);
    setIsSelectionModeActive(false);
  }

  async function openPaymentFlow(ad: Ad) {
    if (!ad.isPayFromBundleAvailable) {
      customPush(`${ad.path}#${QUERY.awaitingPayment}`);
      return;
    }

    if (userInfo?.currentUser?.isActiveShop) {
      await payAdByBalance(ad);
      return;
    }

    setPayByBalanceAd(ad);
    togglePayByBalance(true);
  }

  function closePayByBalanceFlow() {
    togglePayByBalance(false);
    setPayByBalanceAd(null);
  }

  async function payAdByBalance(ad: Ad) {
    const { data } = await payForAdByPrepaidBundle({
      variables: { adId: ad.id },
    });
    const result = data?.payForAdByPrepaidBundle;
    const isNoSpendableItemsError = result?.errors?.some(
      error => error.message === BACKEND_ERRORS.NO_SPENDABLE_BUNDLE_ITEMS
    );

    if (isNoSpendableItemsError) {
      closePayByBalanceFlow();
      customPush(`${ad.path}#${QUERY.awaitingPayment}`);
      return;
    }

    if (!result?.entity) return;

    await Promise.all([
      refetchCounters(),
      refetchUserInfo(),
      client.refetchQueries({
        include: [GET_MY_ADS_LIST],
      }),
    ]);
    closePayByBalanceFlow();
  }

  function confirmPayByBalance() {
    if (!payByBalanceAd || isPayByBalanceLoading) return;

    payAdByBalance(payByBalanceAd);
  }

  function handlePaymentClick(ad: Ad) {
    openPaymentFlow(ad);
  }

  function openRestoreFlow(ad: Ad) {
    customPush(`${ad.path}#${QUERY.prolong}`);
  }

  function openEditPage(ad: Ad) {
    if (isEditPendingApprovalLoading) return;

    checkHasEditPendingApproval({
      variables: { legacyId: String(ad.legacyResourceId) },
    }).then(({ data, client: queryClient }) => {
      const hasEditPendingApproval = data?.ad?.hasEditPendingApproval ?? false;

      if (hasEditPendingApproval) {
        toggleEditPendingApproval(true);
        return;
      }

      if (ad.editLockedUntil) {
        resetHasEditPendingApproval(queryClient, ad.id);
        customPush(`${ad.path}#${EditPopupType.Error}`);
        return;
      }

      customPush(`${ad.path}/${EDIT_AD_PAGE_SLUG}`);
    });
  }

  function closeEditPendingApproval() {
    toggleEditPendingApproval(false);
  }

  function openRemoveAdFlow(ad: Ad) {
    if (isDeleteReasonsLoading) return;

    setRemovingAd(ad);
    setDeleteReasons([]);

    getDeleteReasons({ variables: { id: ad.id } })
      .then(({ data }) => {
        const reasons = data?.node?.deleteReasons;

        if (!reasons?.length) {
          setRemovingAd(null);
          return;
        }

        setDeleteReasons(reasons);
      })
      .catch(() => {
        setRemovingAd(null);
      });
  }

  function closeRemoveAdFlow() {
    setRemovingAd(null);
    setDeleteReasons([]);
  }

  function openArchiveAdFlow(ad: Ad) {
    setArchivingAd(ad);
  }

  function closeArchiveAdFlow() {
    setArchivingAd(null);
  }

  function openRestore() {
    setSelectionMode(SelectionModes.Restore);
    setIsSelectionModeActive(true);
  }

  function closeRestore() {
    setSelectionMode(SelectionModes.None);
    setIsSelectionModeActive(false);
  }

  async function handlePromotionSuccess(): Promise<void> {
    await Promise.all([
      refetchCounters(),
      refetchUserInfo(),
      client.refetchQueries({
        include: [GET_MY_ADS_LIST],
      }),
    ]);
  }

  function handleRestoreSuccess() {
    handlePromotionSuccess();
    client.refetchQueries({ include: [GET_BALANCE] });
  }

  function handleBulkPromotionSuccess() {
    closeBulkPromotion();
    handlePromotionSuccess();
    refetchUserInfo();
  }

  async function removeAd(data: RemoveAdPayload) {
    if (!removingAd) return false;

    const destroyResult = await destroyAd({
      variables: {
        adId: removingAd.id,
        deleteReasonId: data.id,
        deleteReasonDescription: data.delete_reason_description ?? '',
        source,
      },
      update: (cache, result) => {
        if (!result.data?.destroyAd.entity) return;

        cache.evict({
          id: cache.identify({ __typename: 'Ad', id: removingAd.id }),
        });
        cache.gc();
      },
    });

    if (!destroyResult.data?.destroyAd.entity) return false;

    await refetchCounters();
    return true;
  }

  async function archiveAd() {
    if (!archivingAd) return false;

    const result = await archiveAdMutation({
      variables: { adId: archivingAd.id },
      update: (cache, mutationResult) => {
        if (!mutationResult.data?.archiveAd.entity) return;

        cache.evict({
          id: cache.identify({ __typename: 'Ad', id: archivingAd.id }),
        });
        cache.gc();
      },
    });

    if (!result.data?.archiveAd.entity) return false;

    await refetchCounters();
    closeArchiveAdFlow();
    return true;
  }

  const actionHandlers: MyAdCardActionHandlers = {
    advertise: setPromotionAd,
    edit: openEditPage,
    pay: handlePaymentClick,
    remove: openRemoveAdFlow,
    restore: openRestoreFlow,
  };

  return {
    actionHandlers,
    adStatusCounts,
    archiveAd,
    canBulkPromote,
    canBulkRestore,
    closeBulkPromotion,
    closeArchiveAdFlow,
    closeEditPendingApproval,
    closePayByBalanceFlow,
    closeRemoveAdFlow,
    closePromotionFlow,
    closeRestore,
    confirmPayByBalance,
    handlePromotionSuccess,
    handleRestoreSuccess,
    isAdsCountersLoading,
    isRestoreMode,
    openRestore,
    shopPaidAdsBalance,
    isArchiveAdFlowOpen: Boolean(archivingAd),
    isArchiveAdLoading,
    isDeleteReasonsLoading,
    isEditPendingApprovalOpen,
    isPayByBalanceLoading,
    isPayByBalanceOpen,
    isRemoveAdFlowOpen: Boolean(removingAd),
    isBulkPromotionMode,
    openArchiveAdFlow,
    openBulkPromotion,
    deleteReasons,
    handleBulkPromotionSuccess,
    promotionAd,
    payByBalanceAd,
    removeAd,
    selectedAdStatus,
    tabItems,
  };
}
